import React from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TDeviceSelectProps } from '@/console/features/devices/components/device-select/device-select.types';
import { useDeviceSelectOptions } from '@/console/features/devices/hooks/use-device-select-options/use-device-select-options';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import { useReadAllDevicesQuery } from '@/shared/redux/api/assets/devices/devices-api';
import {
  DeviceFragment,
  ReadAllDevicesQuery,
} from '@/shared/types/types.generated';

export const DeviceSelect = <
  TFieldValues extends FieldValues,
  TOptionShape = string,
>({
  name,
  deviceFilter,
  getItemId,
  createOption,
  disableTags = true,
  enableTagsTooltip,
  filterSelectedTags,
  additionalOptions = [],
  onSelectChange,
  required,
  rtkQueryArgs,
  optionProps,
  disabled,
  multiple = true,
}: TDeviceSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();
  const { getAdditionalInfo, isDisabled } = useDeviceSelectOptions(optionProps);

  const createDefaultOption = (device: DeviceFragment) => {
    return {
      label: device.name,
      value: device.deviceId,
      additionalInfo: getAdditionalInfo(device),
      disabled: isDisabled(device),
    } as TSelectOption<TOptionShape>;
  };
  const _createOption = createOption ? createOption : createDefaultOption;

  const transformData = (data?: ReadAllDevicesQuery) => {
    if (!data) {
      return [];
    }

    const devices = data.readAllDevices.items;
    const validDevices = deviceFilter
      ? devices.filter((device) => deviceFilter(device))
      : devices;

    const options = validDevices.map((device) => _createOption(device));

    return [...additionalOptions, ...options];
  };

  return (
    <AsyncSearchableSelect<TFieldValues, ReadAllDevicesQuery, TOptionShape>
      name={name}
      label={t('device:select_devices', { count: multiple ? 2 : 1 })}
      placeholder={t('device:search_devices', { count: multiple ? 2 : 1 })}
      disableTags={disableTags}
      enableTagsTooltip={enableTagsTooltip}
      filterSelectedTags={filterSelectedTags}
      multiple={multiple}
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadAllDevicesQuery,
        transformData,
        rtkQueryArgs,
      }}
      onSelectChange={onSelectChange}
      required={required}
      disabled={disabled}
    />
  );
};
